<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div
    class="app-container flex w-full text-base text-gray-80"
    :class="[
      lightBackground ? 'bg-white' : 'bg-nav-background',
      session.features.zendeskUiNavigation ? 'h-full' : 'fixed',
    ]"
  >
    <global-events
      v-if="showSidebar && !session.features.zendeskUiNavigation"
      @keydown.ctrl.p.prevent="newManualPin"
      @keydown.meta.p.prevent="newManualPin"
      @keydown.ctrl.k.prevent="toggleCommandPalette"
      @keydown.meta.k.prevent="toggleCommandPalette"
    />
    <global-events
      v-else-if="showSidebar"
      @keydown.ctrl.p.prevent="newManualPin"
      @keydown.meta.p.prevent="newManualPin"
    />

    <application-banners
      v-if="session.account && session.user && !isExtension && !session.features.zendeskUiNavigation"
      @padding-change="changeBannerPadding"
    />

    <navbar v-if="showSidebar && !session.features.zendeskUiNavigation" />

    <article
      data-testid="view-container"
      class="flex flex-1"
      :class="[
        isExtension || session.features.zendeskUiNavigation ? 'w-full' : $style.wrapper,
        session.features.zendeskUiNavigation ? 'h-full bg-nav-zd-gray' : '',
      ]"
    >
      <pin-modal />
      <!-- <transition-fade> FIXME: backdrop-filter causes buggy background chopping with fade transition -->
      <command-palette v-if="!session.features.zendeskUiNavigation && showPalette" @close="showPalette = false" />
      <!-- </transition-fade> -->
      <help-drawer />

      <router-view />
    </article>

    <q-error-cat v-if="!session.features.zendeskUiNavigation" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { PinReference_Type } from '@zendesk/zqa-services/pins'
import { useRoute, START_LOCATION } from 'vue-router'

import { GlobalEvents } from '@klausapp/ui-kit'
import { routeMetavalue } from '@/utils/routes'
import analytics, { track } from '@/utils/analytics'
import { openPinModal } from '@/composables/usePinModal'
import { session } from '@/composables/useSession'
import { useEvent } from '@/composables/useEvent'
import PinModal from '@/modules/pins/modal/PinModal.vue'
import { showPalette } from '@/modules/application/components/CommandPalette/utils/useCommandPalette'
import { relativeTimeLater } from '@/utils/date'
import { embeddedRuntime } from '@/config'

import QErrorCat from './components/QErrorCat.vue'
import Navbar from './components/Navbar.vue'
import CommandPalette from './components/CommandPalette'
import ApplicationBanners from './components/ApplicationBanners.vue'
import HelpDrawer from './components/HelpDrawer/HelpDrawer.vue'
import { listenForAppUpdate } from './utils/updateNotification'

const route = useRoute()
const bannerPadding = ref('0px')

const contentHeight = computed(() => {
  return session.features.zendeskUiNavigation
    ? '100%' // Simple 100% height when Zendesk navigation is enabled
    : 'calc(var(--layout-height) - var(--page-header-height) - var(--border) * 2)' // Original calculated height
})

// TODO: Add watcher for route changes to update --app-background CSS var
const lightBackground = computed(() => routeMetavalue(route, 'lightBackground'))

const showSidebar = computed(() => {
  const initialRoute = route.fullPath === START_LOCATION.fullPath
  return session.user && !initialRoute && !routeMetavalue(route, 'hideNav')
})

const isExtension = computed(() => {
  return embeddedRuntime() || route.name?.toString().startsWith('extension.')
})

listenForAppUpdate()

onMounted(() => {
  relativeTimeLater()

  document.addEventListener('click', trackClicks)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', trackClicks)
})

const changeBannerPadding = (padding: string) => {
  bannerPadding.value = padding
}

const newManualPin = () => {
  openPinModal({ referenceType: PinReference_Type.MANUAL })
}

const trackClicks = (e: MouseEvent) => {
  if (!(e.target instanceof HTMLElement)) return
  const { dataset, baseURI, textContent } = e.target
  let name = dataset.trackId
  if (!name) return

  const useMethod = name.startsWith('track:')
  if (useMethod) {
    const method = name.replace('track:', '')
    return (analytics as any)[method]
  }

  track(name, { baseURI, textContent: (textContent || '').replace(/\s+/g, '') })
}

const toggleCommandPalette = () => {
  showPalette.value = !showPalette.value
}

useEvent('show-command-palette', toggleCommandPalette)

watch(lightBackground, (isLight) => {
  // TODO: Get values from Tailwind config
  document.documentElement.style.setProperty('--app-background', isLight ? 'white' : '#171A22')
})
</script>

<style src="@klausapp/ui-kit/styles/tailwind.css"></style>

<style src="@klausapp/ui-kit/styles/variables.css"></style>

<style src="@klausapp/ui-kit/styles/global.css"></style>

<style src="@/styles/settings.scss" lang="scss"></style>

<style src="@/styles/sweetalert2.scss" lang="scss"></style>

<style src="@/styles/tippy.scss" lang="scss"></style>

<style lang="scss">
:root {
  --app-background: theme('colors.gray.90');
  --navbar-width: 4rem;
  --pagenav-width: 14.5rem;
  --left-sidebar-width: 20rem;
  --right-sidebar-width: 20rem;
  --content-sidebar-width: 20rem;
  --page-header-height: 3.5rem;
}

html {
  background-color: var(--app-background);
  background-color: var(--white);
}

.app-container {
  --layout-height: calc(100vh - var(--banner-padding));
  --banner-padding: v-bind(bannerPadding);
  --border: 8px;
  --content-height: v-bind(contentHeight);
  padding-top: var(--banner-padding);

  .main {
    height: var(--layout-height);
  }
}

.va-popup-btn {
  align-items: center;
  justify-content: center;
  display: flex;
  padding-top: 0;
}

button,
a,
label {
  &[data-track-id] * {
    // prevents children of the tracked element from being set as "event.target"
    pointer-events: none;
  }
}
</style>

<style module>
.wrapper {
  max-width: calc(100vw - var(--navbar-width));
}
</style>
